'use server'

import { headers } from 'next/headers'
import { createClient, createServiceClient } from '@repo/db/server'
import { panelHesabiBul, panelHesaplariEpostayla, normalizePanelEmail } from '@repo/db/panel-hesap'
import { hashPublicRateLimitIdentity } from '@repo/db/public-rate-limit'
import { requestClientIp } from '@/lib/password-reset'
import { resolvePanelLogin } from '@/lib/panel-login'
import { consumeGirisIp, waitGirisDelay } from '@/lib/giris-siniri'

const LIMITED = 'Çok fazla deneme yapıldı. Birkaç dakika sonra tekrar deneyin.'
const INVALID = 'E-posta veya şifre hatalı.'
type Result = { success: true; mfa: boolean } | { success: false; error: string }

export async function signInToPanel(rawEmail: string, password: string): Promise<Result> {
  const requestHeaders = await headers()
  const tenantId = requestHeaders.get('x-tenant-id') || null
  const ip = await requestClientIp()
  if (!consumeGirisIp(ip)) return { success: false, error: LIMITED }
  const service = createServiceClient()
  // New forward-only RPC; generated Database types follow the coordinated schema rollout.
  const limits = service as unknown as { rpc: (name: string, args: Record<string, unknown>) => Promise<{ data: { kilitli: boolean; gecikme_ms: number } | null; error: unknown }> }
  let email: string
  try { email = normalizePanelEmail(rawEmail) } catch { email = '' }
  try {
    const account = await resolvePanelLogin(tenantId, email, {
      find: (id, address) => panelHesabiBul(service, id, address),
      all: (address) => panelHesaplariEpostayla(service, address),
      get: (id, address) => panelHesabiBul(service, id, address),
    })
    if (account === 'multiple') return { success: false, error: 'Bu e-posta birden fazla panelde kayıtlı. Panelinizin adresinden giriş yapın.' }
    const identity = `${account?.tenantId ?? tenantId ?? 'main'}\u0000${email || 'invalid'}`
    const accountHash = hashPublicRateLimitIdentity('admin:panel-login-account-v2', identity)
    const ipHash = hashPublicRateLimitIdentity('admin:panel-login-ip-v2', `${identity}\u0000${ip}`)
    // One atomic counter call after Auth: failure increments both keys; success
    // checks the IP lock before cleanup, so a restart cannot bypass a DB lock.
    const updateLimit = async (action: 'fail' | 'success'): Promise<boolean> => {
      try {
        const { data: limit, error } = await limits.rpc('panel_login_failure_limit', {
          p_account_hash: accountHash, p_ip_hash: ipHash, p_action: action,
        })
        if (error || !limit || typeof limit.kilitli !== 'boolean' ||
            !Number.isFinite(limit.gecikme_ms) || limit.gecikme_ms < 0) throw new Error('counter unavailable')
        if (limit.kilitli) return false
        if (limit.gecikme_ms > 0) console.warn('panel-login account pressure', accountHash.slice(0, 12))
        return await waitGirisDelay(limit.gecikme_ms)
      } catch {
        console.warn('panel-login counter unavailable', accountHash.slice(0, 12))
        return true
      }
    }
    if (!account || !password) {
      if (!await updateLimit('fail')) return { success: false, error: LIMITED }
      return { success: false, error: INVALID }
    }

    const supabase = await createClient()
    const { data, error } = await supabase.auth.signInWithPassword({ email: account.loginEmail, password })
    if (error || data.user?.id !== account.userId) {
      if (data.user) await supabase.auth.signOut({ scope: 'local' })
      if (!await updateLimit('fail')) return { success: false, error: LIMITED }
      if (error?.code === 'email_not_confirmed') return { success: false, error: 'Önce şifrenizi belirleyin. Panel yöneticinizden bağlantı isteyin.' }
      return { success: false, error: INVALID }
    }
    if (!await updateLimit('success')) {
      await supabase.auth.signOut({ scope: 'local' })
      return { success: false, error: LIMITED }
    }
    const { data: aal, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
    if (aalError || !aal) {
      await supabase.auth.signOut({ scope: 'local' })
      return { success: false, error: 'Doğrulama durumu alınamadı. Tekrar deneyin.' }
    }
    return { success: true, mfa: aal.nextLevel === 'aal2' && aal.currentLevel === 'aal1' }
  } catch {
    return { success: false, error: 'Sayfayı yenileyip tekrar deneyin.' }
  }
}
